import type { FC } from 'react';
import { Button, ContainedList, HeaderPanel, InlineLoading, SkeletonText } from '@carbon/react';
import { ArrowRight, CheckmarkOutline } from '@carbon/icons-react';

import type { PropsDasNotificacoes } from '../telas/conta/Notificacoes/Notificacoes';
import { ItemDeNotificacao } from '../telas/conta/Notificacoes/ItemDeNotificacao';
import { AvisoComAcao } from '../padroes/AvisoComAcao';
import { ROTULOS, rotuloDoAviso } from '../padroes/rotulos';
import styles from './Casca.module.scss';

// AS NOTIFICAÇÕES NO SINO — um painel da direita do UI Shell, como o da conta: a pessoa confere os
// avisos sem sair do que está fazendo. Os mesmos grupos e itens da página; abrir um aviso marca como
// lido e segue o link (e fecha o painel). "Marcar tudo como lido" fica no alto; limpar, que pede
// confirmação, e o histórico inteiro moram na página ("Ver todas as notificações").
//
// O conteúdo só existe aberto: fechado, nem a lista nem o canal ao vivo dela estão montados.

export type NotificacoesDoPainel = Pick<PropsDasNotificacoes,
  'carregando' | 'falhou' | 'vazio' | 'tentarDeNovo' | 'grupos' | 'temMais' | 'carregandoMais' | 'naoLidas' | 'carregarMais' | 'marcarTodas' | 'abrir'>;

export const ConteudoDasNotificacoes: FC<{ n: NotificacoesDoPainel; verTodas: () => void; aoAbrir: () => void }> = ({ n, verTodas, aoAbrir }) => {
  const temAvisos = !n.carregando && !n.vazio && !n.falhou;
  return (
    <>
      <div className={styles.topoDoPainel}>
        <h2 className={styles.tituloDoPainel}>Notificações</h2>
        {temAvisos && (
          <Button kind='ghost' size='sm' renderIcon={CheckmarkOutline} disabled={n.naoLidas === 0} onClick={n.marcarTodas}>
            Marcar tudo como lido
          </Button>
        )}
      </div>
      <div className={styles.corpoDoPainel}>
        {n.carregando && <div className={styles.recheioDoPainel}><SkeletonText paragraph lineCount={5} /></div>}
        {n.falhou && (
          <div className={styles.recheioDoPainel}>
            <AvisoComAcao kind='error' inline lowContrast hideCloseButton title='Não conseguimos carregar suas notificações.'
              subtitle='Confira a conexão e tente de novo.' actionButtonLabel='Tentar de novo' onActionButtonClick={n.tentarDeNovo}
              statusIconDescription={rotuloDoAviso('error')} aria-label={ROTULOS.fecharAviso} />
          </div>
        )}
        {!n.carregando && n.vazio && (
          <div className={styles.recheioDoPainel}>
            <p className={styles.tituloDoVazio}>Nenhuma notificação</p>
            <p className={styles.apoioDoVazio}>Quando houver um lembrete da agenda, um convite ou um aviso da conta, ele aparece aqui.</p>
          </div>
        )}
        {temAvisos && n.grupos.map((g) => (
          <ContainedList key={g.artistId} label={g.artistName} kind='disclosed'>
            {g.notifications.map((item) => (
              <ItemDeNotificacao key={item.id} n={item} empilhado abrir={(x) => { aoAbrir(); n.abrir(x); }} />
            ))}
          </ContainedList>
        ))}
        {temAvisos && n.temMais && (
          <div className={styles.recheioDoPainel}>
            {n.carregandoMais
              ? <InlineLoading description='Carregando mais…' iconDescription={ROTULOS.carregando} />
              : <Button kind='ghost' size='sm' onClick={n.carregarMais}>Carregar mais</Button>}
          </div>
        )}
      </div>
      <div className={styles.acaoDoPainel}>
        <Button kind='ghost' size='md' renderIcon={ArrowRight} onClick={() => { aoAbrir(); verTodas(); }}>Ver todas as notificações</Button>
      </div>
    </>
  );
};

export const PainelDeNotificacoes: FC<{ aberto: boolean; conteudo: React.ReactNode }> = ({ aberto, conteudo }) => (
  <HeaderPanel expanded={aberto} aria-label='Notificações' className={`${styles.painel} ${styles.painelLargo}`}>
    {aberto && conteudo}
  </HeaderPanel>
);
